import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoggerService } from '../../../services/logger.service';

export interface AdjustmentDialogData {
  enrollmentId: string;
  enrollment?: any;
}

export interface Adjustments {
  used: number;
  available: number;
  notes: string;
}

@Component({
  selector: 'app-lesson-adjustment-dialog',
  templateUrl: './lesson-adjustment-dialog.component.html',
  styleUrls: ['./lesson-adjustment-dialog.component.css']
})
export class LessonAdjustmentDialogComponent implements OnInit {
  
  adjustments: Adjustments = {
    used: 0,
    available: 0,
    notes: ''
  };

  isSaving = false;

  constructor(
    public dialogRef: MatDialogRef<LessonAdjustmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AdjustmentDialogData,
    private entityRESTService: EntityRESTService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    // Load current adjustment values if they exist
    this.loadCurrentAdjustments();
  }

  private loadCurrentAdjustments(): void {
    if (this.data.enrollment) {
      // Handle both list view data format and full entity data format
      this.adjustments.used = this.getFieldValue(this.data.enrollment.field_lesson_adj_used);
      this.adjustments.available = this.getFieldValue(this.data.enrollment.field_lesson_adj_available);
      this.adjustments.notes = this.getFieldValue(this.data.enrollment.field_lesson_adj_notes, '');
    }
  }

  private getFieldValue(fieldData: any, defaultValue: any = 0): any {
    if (!fieldData) return defaultValue;
    
    // Handle Drupal field format: [{ value: "123" }]
    if (Array.isArray(fieldData) && fieldData.length > 0 && fieldData[0].value !== undefined) {
      const value = fieldData[0].value;
      return defaultValue === '' ? value : (isNaN(value) ? defaultValue : Number(value));
    }
    
    // Handle direct value format
    return fieldData;
  }

  hasAnyAdjustments(): boolean {
    return this.adjustments.used !== 0 || 
           this.adjustments.available !== 0;
  }

  getCurrentValue(fieldName: string): any {
    if (!this.data.enrollment) return 0;
    
    return this.getFieldValue(this.data.enrollment[fieldName]);
  }

  getPreviewValue(type: 'used' | 'available'): number {
    if (!this.data.enrollment) return 0;
    
    const fieldMap = {
      used: 'field_lesson_used',
      available: 'field_lesson_available'
    };
    
    const currentStoredValue = this.getFieldValue(this.data.enrollment[fieldMap[type]]);
    
    // Subtract current adjustment to get base, then add new adjustment
    const currentAdjustment = this.getCurrentAdjustment(type);
    const originalValue = currentStoredValue - currentAdjustment;
    return originalValue + this.adjustments[type];
  }

  private getCurrentAdjustment(type: 'used' | 'available'): number {
    if (!this.data.enrollment) return 0;
    
    const fieldMap = {
      used: 'field_lesson_adj_used',
      available: 'field_lesson_adj_available'
    };
    
    return this.getFieldValue(this.data.enrollment[fieldMap[type]]);
  }

  getChangeText(type: 'used' | 'available'): string {
    const change = this.adjustments[type];
    if (change === 0) return '';
    return change > 0 ? `(+${change})` : `(${change})`;
  }

  getChangeClass(type: 'used' | 'available'): string {
    const change = this.adjustments[type];
    if (change > 0) return 'positive-change';
    if (change < 0) return 'negative-change';
    return '';
  }

  onReset(): void {
    this.adjustments = {
      used: 0,
      available: 0,
      notes: ''
    };
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.isSaving = true;

    const patchData = {
      field_lesson_adj_used: [{ value: this.adjustments.used.toString() }],
      field_lesson_adj_available: [{ value: this.adjustments.available.toString() }],
      field_lesson_adj_notes: [{ value: this.adjustments.notes }]
    };

    this.entityRESTService.patchEntity('packages', 'enrollment', +this.data.enrollmentId, patchData).subscribe({
      next: (response: any) => {
        this.snackBar.open('Lesson adjustments saved successfully', 'Close', {
          duration: 3000
        });
        this.dialogRef.close(true); // Return true to indicate success
      },
      error: (error: any) => {
        LoggerService.error('Error saving lesson adjustments:', error);
        this.snackBar.open('Error saving adjustments. Please try again.', 'Close', {
          duration: 5000
        });
        this.isSaving = false;
      }
    });
  }
}